import { FullPaymentDueReport, PaymentDueReport, PaymentDueReportRow, PaymentScope } from "@/app/vendors/payment-dues/payment-dues.entity";
import { uuid } from "@/interfaces";
import { api } from "@/lib/api";
import { activeOrganizationId } from "@/lib/active-organization";
import { requirePermission } from "@/lib/permissions";
import { PaymentDue, PaymentRecipientType } from "@/models/payment-dues.model";
import { DUE_SCOPE, RECIPIENT_KIND } from "@/services/stripe-subs-payments.service";
import { PAYOUT_PAGE_SIZE_MAX, type PaymentDue as ApiPaymentDue, type PaymentDueItem, type PaymentRecipientKind, type Payout, type PayoutDetail } from "@shyne/shared/api";

type DateRangeFilter = {
  fromDate?: string | null;
  toDate?: string | null;
};

const RECIPIENT_TYPE: Record<PaymentRecipientKind, PaymentRecipientType> = { worker: "individual", subcontractor: "subs" };

const rangeQuery = (dateRange?: DateRangeFilter) => ({
  ...(dateRange?.fromDate ? { from: dateRange.fromDate } : {}),
  ...(dateRange?.toDate ? { to: dateRange.toDate } : {}),
});

const toPaymentDue = (due: ApiPaymentDue): PaymentDue => {
  const recipientType = RECIPIENT_TYPE[due.recipient.kind];
  return {
    id: due.recipient.id,
    name: due.recipient.name,
    remaining_amount: due.owed.amount,
    appointment_count: due.owed.appointments.count,
    daily_work_count: due.owed.workDays.count,
    remaining_appointment_amount: due.owed.appointments.amount,
    remaining_daily_work_amount: due.owed.workDays.amount,
    total_amount: due.completed.amount,
    total_appointment_amount: due.completed.appointments.amount,
    total_daily_work_amount: due.completed.workDays.amount,
    total_appointment_count: due.completed.appointments.count,
    total_daily_work_count: due.completed.workDays.count,
    ready_for_payouts: due.readyForPayouts === true,
    payment_terms_days: due.paymentTermsDays,
    recipient_type: recipientType,
    recipient_user_id: recipientType === "individual" ? due.recipient.id : null,
    recipient_org_id: recipientType === "subs" ? due.recipient.id : null,
    vendor_org_id: due.payer.id,
  };
};

export type PaymentDuesByRecipient = Record<PaymentRecipientType, PaymentDue[]>;

async function paymentDuesOwed(dateRange?: DateRangeFilter): Promise<PaymentDuesByRecipient> {
  const payer = activeOrganizationId();
  const { items } = await api.paymentDues.list({ actingOrganization: payer, ...rangeQuery(dateRange) });
  const owed = items.filter((due) => due.payer.id === payer).map(toPaymentDue);
  return { individual: owed.filter((due) => due.recipient_type === "individual"), subs: owed.filter((due) => due.recipient_type === "subs") };
}

function toReportRow(item: PaymentDueItem): PaymentDueReportRow {
  const property = item.property?.name ?? null;
  const descriptor = property ? ` - ${property}` : "";
  if (item.kind === "appointment") {
    return {
      id: item.id,
      type: "appointment",
      amount: item.amount,
      detailPath: `/appointments/${item.id}`,
      label: `Appointment ${item.id.slice(0, 8)}${descriptor}`,
      propertyName: property,
      unitNumber: item.unit,
      date: item.date,
      paymentStatus: item.status,
    };
  }
  const contract = item.contract ?? undefined;
  return {
    id: item.id,
    type: "daily_work_task",
    amount: item.amount,
    detailPath: contract ? `/dailywork/${contract}` : "/dailywork/contracts",
    label: `Daily Work ${contract ? contract.slice(0, 8) : "N/A"} (Task ${item.id.slice(0, 8)})${descriptor}`,
    propertyName: property,
    unitNumber: null,
    parentId: contract,
    date: item.date,
    paymentStatus: item.status,
    hoursWorked: item.hoursWorked,
  };
}

async function outstandingReport(recipientType: PaymentRecipientType, recipientId: uuid, dateRange?: DateRangeFilter): Promise<PaymentDueReport> {
  const payer = activeOrganizationId();
  const { items, amount } = await api.paymentDues.outstanding(RECIPIENT_KIND[recipientType], recipientId, { payer, actingOrganization: payer, ...rangeQuery(dateRange) });
  return { items: items.map(toReportRow), totalAmount: amount };
}

async function fullItemReport(recipientType: PaymentRecipientType, recipientId: uuid, dateRange?: DateRangeFilter): Promise<FullPaymentDueReport> {
  const payer = activeOrganizationId();
  const report = await api.paymentDues.items(RECIPIENT_KIND[recipientType], recipientId, { payer, actingOrganization: payer, ...rangeQuery(dateRange) });
  return { items: report.items.map(toReportRow), totalAmount: report.amount, paidAmount: report.paid, unpaidAmount: report.unpaid };
}

export const PaymentDuesRepository = {
  async getPaymentDuesForVendor(dateRange?: DateRangeFilter): Promise<PaymentDuesByRecipient> {
    await requirePermission("payment_dues.view");
    return paymentDuesOwed(dateRange);
  },

  async getIndividualPaymentDuesForSubs(): Promise<PaymentDue[]> {
    await requirePermission("payment_dues.view");
    return (await paymentDuesOwed()).individual.filter((due) => due.remaining_amount > 0);
  },

  async updatePaymentTerms(recipientType: PaymentRecipientType, recipientId: uuid, paymentTermsDays: number): Promise<void> {
    await requirePermission("payment_dues.view");
    const payer = activeOrganizationId();
    await api.paymentDues.setPaymentTerms(RECIPIENT_KIND[recipientType], recipientId, { payer, actingOrganization: payer, days: paymentTermsDays });
  },

  async markDuesPaid(recipientType: PaymentRecipientType, recipientId: uuid, dateRange?: DateRangeFilter, paymentScope: PaymentScope = "both"): Promise<void> {
    await requirePermission("payment_dues.view");
    const payer = activeOrganizationId();
    await api.paymentDues.markPaid(RECIPIENT_KIND[recipientType], recipientId, { payer, actingOrganization: payer, ...rangeQuery(dateRange), scope: DUE_SCOPE[paymentScope] });
  },

  async getIndividualPaymentDueReport(individualId: uuid, dateRange?: DateRangeFilter): Promise<PaymentDueReport> {
    await requirePermission("payment_dues.view");
    return outstandingReport("individual", individualId, dateRange);
  },

  async getIndividualPaymentDueReportForSubs(individualId: uuid): Promise<PaymentDueReport> {
    await requirePermission("payment_dues.view");
    return outstandingReport("individual", individualId);
  },

  async getSubsPaymentDueReport(subOrgId: uuid, dateRange?: DateRangeFilter): Promise<PaymentDueReport> {
    await requirePermission("payment_dues.view");
    return outstandingReport("subs", subOrgId, dateRange);
  },

  async getIndividualFullItemReport(individualId: uuid, dateRange?: DateRangeFilter): Promise<FullPaymentDueReport> {
    await requirePermission("payment_dues.view");
    return fullItemReport("individual", individualId, dateRange);
  },

  async getSubsFullItemReport(subOrgId: uuid, dateRange?: DateRangeFilter): Promise<FullPaymentDueReport> {
    await requirePermission("payment_dues.view");
    return fullItemReport("subs", subOrgId, dateRange);
  },

  async getPaymentHistory(recipientId: uuid): Promise<Payout[]> {
    await requirePermission("payment_dues.view");
    const payer = activeOrganizationId();
    const history: Payout[] = [];
    for (let page = 1; ; page++) {
      const { items, total } = await api.payouts.list({ actingOrganization: payer, payer, recipient: recipientId, page, pageSize: PAYOUT_PAGE_SIZE_MAX });
      history.push(...items);
      if (items.length < PAYOUT_PAGE_SIZE_MAX || history.length >= total) return history;
    }
  },

  async getPayoutDetail(payoutId: uuid): Promise<PayoutDetail> {
    await requirePermission("payment_dues.view");
    return api.payouts.get(payoutId, { actingOrganization: activeOrganizationId() });
  },
};
